export const DEFAULT_CART_ADD_ERROR_MESSAGE =
  "Failed to add product to cart. Please try again.";

const TOAST_HOST_ID = "weupsell-toast-host";
const TOAST_STYLE_ID = "weupsell-toast-styles";
const ERROR_TOAST_ID = "weupsell-cart-add-error";
const SUCCESS_TOAST_ID = "weupsell-cart-add-success";

let dismissTimer = null;

/**
 * Parse Shopify /cart/add.js or /cart/change.js error body (422 etc.) into a user-facing message.
 * @param {Response} response
 * @returns {Promise<string>}
 */
export async function parseCartAddErrorFromResponse(response) {
  const fallback = DEFAULT_CART_ADD_ERROR_MESSAGE;
  const isChange =
    typeof response?.url === "string" &&
    response.url.includes("/cart/change");

  try {
    const body = await response.json();
    const message =
      body?.description ||
      body?.message ||
      (Array.isArray(body?.errors) ? body.errors.filter(Boolean).join(". ") : null) ||
      (body?.errors && typeof body.errors === "object"
        ? Object.values(body.errors).flat().filter(Boolean).join(". ")
        : null);

    if (message && String(message).trim()) {
      return String(message).trim();
    }
  } catch (_) {
    // Response body may not be JSON.
  }

  if (response?.status === 422) {
    return isChange
      ? "This cart update could not be completed. The item may be out of stock or unavailable."
      : "This item could not be added to your cart. It may be out of stock or unavailable.";
  }

  return isChange
    ? "Failed to update cart. Please try again."
    : fallback;
}

/**
 * @param {unknown} error
 * @returns {string}
 */
export function getCartAddErrorMessage(error) {
  if (!error) return DEFAULT_CART_ADD_ERROR_MESSAGE;

  const message =
    typeof error === "string" ? error : error?.message || error?.description;

  if (
    typeof message === "string" &&
    message.trim() &&
    !message.startsWith("HTTP error!")
  ) {
    return message.trim();
  }

  return DEFAULT_CART_ADD_ERROR_MESSAGE;
}

function ensureToastStyles() {
  if (typeof document === "undefined") return;
  if (document.getElementById(TOAST_STYLE_ID)) return;

  const style = document.createElement("style");
  style.id = TOAST_STYLE_ID;
  style.textContent = `
@keyframes weupsell-toast-in {
  from { opacity: 0; transform: translate(-50%, -12px); }
  to { opacity: 1; transform: translate(-50%, 0); }
}
@keyframes weupsell-toast-out {
  from { opacity: 1; transform: translate(-50%, 0); }
  to { opacity: 0; transform: translate(-50%, -12px); }
}
#${TOAST_HOST_ID} {
  position: fixed !important;
  inset: 0 !important;
  width: 0 !important;
  height: 0 !important;
  z-index: 2147483647 !important;
  pointer-events: none !important;
  overflow: visible !important;
}
.weupsell-toast {
  position: fixed !important;
  top: 24px !important;
  left: 50% !important;
  transform: translateX(-50%) !important;
  display: flex !important;
  align-items: flex-start !important;
  gap: 10px !important;
  max-width: min(420px, calc(100vw - 32px)) !important;
  min-width: 280px !important;
  padding: 12px 14px !important;
  border-radius: 10px !important;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.18), 0 2px 8px rgba(15, 23, 42, 0.12) !important;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  line-height: 1.4 !important;
  letter-spacing: normal !important;
  text-align: left !important;
  color: #fff !important;
  pointer-events: auto !important;
  animation: weupsell-toast-in 180ms ease-out !important;
  box-sizing: border-box !important;
}
.weupsell-toast--error {
  background: #dc2626 !important;
  border: 1px solid #b91c1c !important;
}
.weupsell-toast--success {
  background: #16a34a !important;
  border: 1px solid #15803d !important;
}
.weupsell-toast--leaving {
  animation: weupsell-toast-out 160ms ease-in forwards !important;
}
.weupsell-toast__icon {
  flex: 0 0 auto !important;
  width: 20px !important;
  height: 20px !important;
  margin-top: 1px !important;
}
.weupsell-toast__message {
  flex: 1 1 auto !important;
  margin: 0 !important;
  padding: 0 !important;
  color: #fff !important;
  font: inherit !important;
  text-align: left !important;
  white-space: normal !important;
  word-break: break-word !important;
}
.weupsell-toast__close {
  flex: 0 0 auto !important;
  appearance: none !important;
  border: 0 !important;
  background: transparent !important;
  color: rgba(255,255,255,0.9) !important;
  cursor: pointer !important;
  font-size: 18px !important;
  line-height: 1 !important;
  padding: 0 2px !important;
  margin: 0 !important;
}
`;
  document.head.appendChild(style);
}

function ensureToastHost() {
  if (typeof document === "undefined") return null;
  ensureToastStyles();

  let host = document.getElementById(TOAST_HOST_ID);
  if (!host) {
    host = document.createElement("div");
    host.id = TOAST_HOST_ID;
    host.setAttribute("aria-live", "polite");
  }
  // Re-append so toast sits above WeUpsell shadow modal / cart drawer hosts.
  document.body.appendChild(host);
  host.style.zIndex = "2147483647";
  return host;
}

function removeToastElement(el) {
  if (!el || !el.parentNode) return;
  el.classList.add("weupsell-toast--leaving");
  window.setTimeout(() => {
    if (el.parentNode) el.parentNode.removeChild(el);
  }, 160);
}

function showWeupsellToast(message, type = "error", duration = 4000) {
  if (typeof document === "undefined") return;

  const host = ensureToastHost();
  if (!host) return;

  const toastId = type === "success" ? SUCCESS_TOAST_ID : ERROR_TOAST_ID;
  const existing = host.querySelector(`#${toastId}`);
  if (existing) removeToastElement(existing);

  if (dismissTimer) {
    window.clearTimeout(dismissTimer);
    dismissTimer = null;
  }

  const toast = document.createElement("div");
  toast.id = toastId;
  toast.className = `weupsell-toast weupsell-toast--${type}`;
  toast.setAttribute("role", "status");

  const icon = document.createElement("span");
  icon.className = "weupsell-toast__icon";
  icon.setAttribute("aria-hidden", "true");
  icon.innerHTML =
    type === "success"
      ? `<svg viewBox="0 0 20 20" fill="none" width="20" height="20"><circle cx="10" cy="10" r="9" stroke="white" stroke-width="1.6"/><path d="M6 10.2l2.4 2.4L14 7.2" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`
      : `<svg viewBox="0 0 20 20" fill="none" width="20" height="20"><circle cx="10" cy="10" r="9" stroke="white" stroke-width="1.6"/><path d="M10 6v5.5M10 14h.01" stroke="white" stroke-width="1.8" stroke-linecap="round"/></svg>`;

  const text = document.createElement("p");
  text.className = "weupsell-toast__message";
  text.textContent = message;

  const close = document.createElement("button");
  close.type = "button";
  close.className = "weupsell-toast__close";
  close.setAttribute("aria-label", "Dismiss");
  close.textContent = "×";
  close.addEventListener("click", () => removeToastElement(toast));

  toast.appendChild(icon);
  toast.appendChild(text);
  toast.appendChild(close);
  host.appendChild(toast);

  dismissTimer = window.setTimeout(() => {
    removeToastElement(toast);
    dismissTimer = null;
  }, duration);
}

/**
 * @param {unknown} errorOrMessage
 */
export function showCartAddError(errorOrMessage) {
  const message =
    typeof errorOrMessage === "string"
      ? errorOrMessage
      : getCartAddErrorMessage(errorOrMessage);

  // Hybrid fetch patch + React callers may both fire for the same failure.
  const now =
    typeof performance !== "undefined" && performance.now
      ? performance.now()
      : Date.now();
  if (
    typeof window !== "undefined" &&
    window.__WEUPSELL_LAST_CART_ERROR_TOAST__ &&
    window.__WEUPSELL_LAST_CART_ERROR_TOAST__.message === message &&
    now - window.__WEUPSELL_LAST_CART_ERROR_TOAST__.at < 900
  ) {
    return;
  }
  if (typeof window !== "undefined") {
    window.__WEUPSELL_LAST_CART_ERROR_TOAST__ = { message, at: now };
  }

  showWeupsellToast(message, "error", 4500);
}

/**
 * @param {string} [message]
 */
export function showCartAddSuccess(message = "Added to cart") {
  showWeupsellToast(message, "success", 3000);
}
